/**
 * Whether a notifyUsers result says ANYONE was actually reached on ANY
 * channel. Pure and import-free so tests can pin it directly (the same
 * strip-types pattern as teacherNameMatch.ts / absenceDayGuard.ts).
 *
 * Why this exists (review P1, 2026-08-07): notifyUsers used to THROW when
 * its in-app batch write failed, and roomRequestActions leaned on that
 * throw as its "nobody was told" detector. The notifications pipeline fix
 * made the in-app failure non-fatal - notifyUsers now resolves with an
 * honest inApp count and still tries the server channels - which silently
 * turned "resolved non-null" into a claim it no longer is. Any caller that
 * closes work, flips a card green, or says "they were told" on the strength
 * of a notification must branch on THIS, never on null-ness alone:
 *
 *   null              -> nothing was written and nobody was left to tell
 *   all-zero result   -> a real recipient existed and NO channel reached
 *                        them (in-app write failed AND the server delivered
 *                        nothing) - never a success
 *   any count > 0     -> at least one channel really landed
 */
export function notifyDeliveredAnything(
  result: { inApp: number; email: number; sms: number; push: number } | null
): boolean {
  return !!result && (result.inApp > 0 || result.email > 0 || result.sms > 0 || result.push > 0);
}

/**
 * The authenticated server proved the only recipient is no longer available.
 * This is not delivery, but it is a safe terminal outcome for an old workflow:
 * a deleted/inactive account must not make its calendar booking immutable.
 * Provider errors, preference skips, and a missing phone never qualify.
 */
export function notifyRecipientUnavailable(
  result: {
    inApp: number;
    email: number;
    sms: number;
    push: number;
    skipped: number;
    errors: number;
    serverReached: boolean;
  } | null
): boolean {
  return !!result &&
    result.serverReached &&
    result.skipped > 0 &&
    result.inApp === 0 &&
    result.email === 0 &&
    result.sms === 0 &&
    result.push === 0 &&
    result.errors === 0;
}
